import * as React from 'react';
import { Card, Loading, Table, Input, Page, notify } from 'sha-el-design';
import { nestedAccess } from 'sha-el-design/lib/helpers';
import * as moment from 'moment';
import { Colums } from 'sha-el-design/lib/components/Table/Table';
import { match } from 'react-router';
import { Link } from 'react-router-dom';
import { GraphqlQuery } from 'requestapijs';
import { Pagination } from '../Common/Pagination';
import {
  CallHistoryNodeConnection,
  CallHistoryNodeEdge,
} from '../../../schema';
import {
  DetailCallHistoryReportQuery,
  callerNameQuery,
  summarisedReportQuery,
  IST_DAY_START,
  IST_DAY_END,
} from './constants';
import { statusPill } from './statusPill';
import { num } from './deep';
import {
  FMT, PRESETS, RangePresets, activePresetKey,
  statTile, connectRate, groupNum,
  durationSeconds, shortDuration, decorateRows, clickedRowIndex,
} from './reportBits';
import { MdArrowBack, MdHome, MdChevronRight } from 'react-icons/md';
import '../Common/listPageStyles';

export class DetailCallHistoryReport extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    // Both ends defaulted to today, so drilling into a caller from the
    // summary -- which opens on the last 30 days -- showed an empty table for
    // someone the previous screen had just credited with calls.
    const from = moment().subtract(29, 'days').format(FMT);
    const to = moment().format(FMT);
    this.state = {
      // Draft (what the inputs show) vs applied (what the query runs), so a
      // wide range is fetched when asked for rather than on every keystroke.
      fromDate: from,
      toDate: to,
      retailerName: '',
      appliedFrom: from,
      appliedTo: to,
      appliedName: '',
      after: '',
    };
  }

  rangeIsValid = () => {
    const { fromDate, toDate } = this.state;
    return !!fromDate && !!toDate && !moment(fromDate).isAfter(moment(toDate));
  }

  isDirty = () => {
    const { fromDate, toDate, retailerName, appliedFrom, appliedTo, appliedName } = this.state;
    return fromDate !== appliedFrom || toDate !== appliedTo || retailerName !== appliedName;
  }

  applyPreset = (preset: typeof PRESETS[0]) => {
    const [a, b] = preset.range();
    const fromDate = a.format(FMT);
    const toDate = b.format(FMT);
    // A preset is an explicit choice, so it searches immediately.
    this.setState({
      fromDate, toDate,
      appliedFrom: fromDate, appliedTo: toDate,
      appliedName: this.state.retailerName,
      after: '',
    });
  }

  search = () => {
    if (!this.rangeIsValid()) {
      return notify({
        type: 'error',
        title: 'Check the dates',
        message: 'From Date must be on or before To Date.',
        duration: 3000,
      });
    }
    const { fromDate, toDate, retailerName } = this.state;
    this.setState({
      appliedFrom: fromDate, appliedTo: toDate, appliedName: retailerName, after: '',
    });
  }

  onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { this.search(); }
  }

  private tableRef: HTMLDivElement = null;
  private rows: any[] = [];

  goToQnA = (node: any) => {
    if (!node || node.callStatus !== 'ANSWERED') { return; }
    const to = `/dashboard/retailer_report/${node.id}`;
    (this.props as any).history
      ? (this.props as any).history.push(to)
      : window.location.assign(to);
  }

  columns = (): Array<Colums<CallHistoryNodeEdge>> => [{
    key: 'retailer_name',
    header: 'Retailer',
    render: (text, obj) => (
      <span className='cs-q__name'>
        {nestedAccess(obj.node, 'retailer', 'firm') || 'Unknown retailer'}
      </span>
    ),
  }, {
    key: 'call_date',
    header: 'Called',
    render: (text, obj) => {
      const start = obj.node.callStart;
      return (
        <div>
          <span>{obj.node.callDate ? moment(obj.node.callDate).format('D MMM YYYY') : '—'}</span>
          {start && <p className='cs-q__desc'>{moment(start).format('h:mm a')}</p>}
        </div>
      );
    },
  }, {
    key: 'mobile_number',
    header: 'Mobile Number',
    render: (text, obj) => obj.node.mobileNumber || '—',
  }, {
    key: 'status',
    header: 'Status',
    render: (text, obj) => statusPill(obj.node.callStatus),
  }, {
    key: 'duration',
    header: 'Duration',
    render: (text, obj) => {
      // Every unanswered call reported "0 Minutes 0 Seconds", which is noise
      // in the column you scan to see how long conversations ran.
      if (obj.node.callStatus !== 'ANSWERED') {
        return <span className='cs-rep__num cs-rep__num--zero'>—</span>;
      }
      return (
        <span className='cs-rep__num'>{shortDuration(durationSeconds(obj.node.duration))}</span>
      );
    },
  }, {
    key: 'qna',
    header: '',
    render: (text, obj) => (
      // Only answered calls open the questionnaire, so only they can have a
      // response. Every row used to link through, most of them to an empty
      // screen.
      obj.node.callStatus === 'ANSWERED'
        ? <span className='cs-rep__go'><MdChevronRight /></span>
        : null
    ),
  }]

  breadcrumbs = (callerName: string) => ({
    seperator: '/',
    paths: [
      { text: <MdHome />, link: '/dashboard' },
      { text: 'Call History Report', link: '/dashboard/call_history_report' },
      { text: callerName || 'Caller', link: `/dashboard/call_history_report/${this.props.match.params.id}` },
    ].map((v) => ({
      render: () => (
        <Link key={v.link} to={v.link}>
          {v.text}
        </Link>
      ),
    })),
  })

  // Totals for the WHOLE period, not for the twenty rows on screen. The
  // summarised resolvers already compute exactly these figures for a single
  // caller, so the tiles reuse them rather than adding up one page and
  // calling it a total.
  tiles = () => {
    const { appliedFrom, appliedTo } = this.state;
    return (
      <GraphqlQuery
        queryString={summarisedReportQuery}
        variables={{
          dateRange: `${appliedFrom},${appliedTo}`,
          userId: this.props.match.params.id,
          after: '',
        }}
        render={(response: any, error: any, loading: boolean) => {
          const edges = nestedAccess(response, 'allUserDetails', 'edges');
          if (loading || error || !edges) { return null; }
          const node = edges.length ? edges[0].node : null;
          const answered = node ? num(node.callAnswered) : 0;
          const noReply = node ? num(node.notAnswered) : 0;
          const wrong = node ? num(node.wrongNumber) : 0;
          const alerts = node ? num(node.submitWithAlert) : 0;
          const allocations = node ? num(node.totalAssignment) : 0;
          const calls = answered + noReply + wrong;
          const pct = connectRate(answered, noReply, wrong);
          return (
            <div className='cs-stats'>
              {statTile('calls', 'Calls placed', groupNum(calls), 'in the selected period')}
              {statTile('answered', 'Answered', groupNum(answered),
                `${groupNum(noReply)} no reply · ${groupNum(wrong)} wrong number`)}
              {statTile('rate', 'Connect rate', pct === null ? '—' : `${pct}%`,
                'of dialled numbers answered')}
              {statTile('alert', 'Allocations worked', groupNum(allocations),
                alerts ? `${groupNum(alerts)} response${alerts === 1 ? '' : 's'} flagged`
                       : 'no responses flagged')}
            </div>
          );
        }}
      />
    );
  }

  table = () => {
    const { appliedFrom: fromDate, appliedTo: toDate, appliedName: search, after } = this.state;
    return (
      <GraphqlQuery
        key='query'
        queryString={DetailCallHistoryReportQuery}
        variables={{
          userId: this.props.match.params.id,
          retailerName: search,
          fromDate: `${fromDate}${IST_DAY_START}`,
          toDate: `${toDate}${IST_DAY_END}`,
          after,
        }}
        render={(response: { allCallHistorys: CallHistoryNodeConnection }, error, loading) => {
          const edges = nestedAccess(response, 'allCallHistorys', 'edges');
          if (error || (!loading && !edges)) {
            return (
              <div className='cs-list__empty'>
                <h4>Could not load these calls</h4>
                <p>Check the date range and try again.</p>
              </div>
            );
          }
          return (
            <Loading
              isLoading={loading}
              render={() => {
                this.rows = edges;
                return (
                  <>
                    {edges.length ? (
                      <div
                        className='cs-list--stack'
                        ref={(el) => {
                          this.tableRef = el;
                          if (el) { decorateRows(el, { clickable: true }); }
                        }}
                        onClick={(e) => {
                          const i = clickedRowIndex(this.tableRef, e.target as HTMLElement);
                          if (i >= 0 && this.rows[i]) { this.goToQnA(this.rows[i].node); }
                        }}
                      >
                        <Table data={edges} columns={this.columns()} />
                      </div>
                    ) : (
                      <div className='cs-list__empty'>
                        <h4>No calls in this period</h4>
                        <p>
                          {search
                            ? `No call to a retailer matching "${search}" between these dates.`
                            : 'This caller placed no calls between these dates. Try a wider range — All time shows everything on record.'}
                        </p>
                      </div>
                    )}
                    <Pagination
                      key={`${fromDate}|${toDate}|${search}`}
                      endCursor={nestedAccess(response, 'allCallHistorys', 'pageInfo', 'endCursor')}
                      hasNextPage={nestedAccess(response, 'allCallHistorys', 'pageInfo', 'hasNextPage')}
                      after={(afterValue) => this.setState({ after: afterValue })}
                    />
                  </>
                );
              }}
            />
          );
        }}
      />
    );
  }

  render() {
    const { fromDate, toDate, retailerName, appliedFrom, appliedTo, appliedName } = this.state;
    const validRange = this.rangeIsValid();
    return (
      // The screen used to be a bare Card with no title bar, no breadcrumbs
      // and no way back to the summary except the browser button.
      <GraphqlQuery
        queryString={callerNameQuery}
        variables={{ id: this.props.match.params.id }}
        render={(nameResponse: any) => {
          const callerName = nestedAccess(nameResponse, 'userDetail', 'employee', 'name');
          return (
            <div className='cs-list'>
              <Page
                title={callerName ? `${callerName} — Call History` : 'Call History'}
                breadcrumbs={this.breadcrumbs(callerName)}
                backIcon={
                  <Link to={'/dashboard/call_history_report'}>
                    <MdArrowBack />
                  </Link>
                }
              >
                <Card>
                  <RangePresets
                    activeKey={activePresetKey(appliedFrom, appliedTo)}
                    onPick={this.applyPreset}
                  />

                  {/* .cs-rep__field wrappers: sha-el's Input emits a
                      container AND a help div as siblings, which would
                      otherwise take two grid columns each. */}
                  <div className='cs-rep__filters' onKeyDown={this.onKeyDown}>
                    <div className='cs-rep__field'>
                      <Input
                        type='date'
                        label='From Date'
                        value={fromDate}
                        onChange={(e) => this.setState({ fromDate: e.target.value })}
                      />
                    </div>
                    <div className='cs-rep__field'>
                      <Input
                        type='date'
                        label='To Date'
                        value={toDate}
                        onChange={(e) => this.setState({ toDate: e.target.value })}
                      />
                    </div>
                    <div className='cs-rep__field'>
                      <Input
                        type='text'
                        label='Search Retailer'
                        value={retailerName}
                        onChange={(e) => this.setState({ retailerName: e.target.value })}
                        placeholder='Retailer firm'
                      />
                    </div>
                    <button
                      type='button'
                      className='cs-rep__search'
                      onClick={this.search}
                      disabled={!validRange}
                    >
                      Search
                    </button>
                  </div>

                  <p className='cs-rep__scope'>
                    <span>
                      Calls placed between <b>{moment(appliedFrom).format('D MMM YYYY')}</b>
                      {' '}and <b>{moment(appliedTo).format('D MMM YYYY')}</b>
                      {appliedName ? <> matching <b>{appliedName}</b></> : ''}.
                      {' '}Answered calls open their questionnaire answers.
                    </span>
                    {!validRange && <span className='cs-rep__warn'>From Date is after To Date</span>}
                    {validRange && this.isDirty() &&
                      <span className='cs-rep__dirty'>Press Search to apply</span>}
                  </p>

                  {this.tiles()}
                  {this.table()}
                </Card>
              </Page>
            </div>
          );
        }}
      />
    );
  }
}

interface State {
  fromDate: string;
  toDate: string;
  retailerName: string;
  appliedFrom: string;
  appliedTo: string;
  appliedName: string;
  after: string;
}

interface Props {
  match: match<{ id: string }>;
}
